import { memo, useCallback, type FC } from 'react';
import { EnvironmentOutlined } from '@ant-design/icons';
import { Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { TechnicianNameBadge } from '~/app/components/maintenance/work-orders/TechnicianNameBadge/TechnicianNameBadge';
import type { KanbanWorkOrderView } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import { WorkOrderTicketChip } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/WorkOrderTicketChip/WorkOrderTicketChip';
import type { KanbanActiveDragKind } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/WorkOrderKanbanColumn/WorkOrderKanbanColumn';
import {
  WORK_ORDER_UI,
  softBadgeStyle,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import {
  WorkOrderCardDragHandle,
  WorkOrderDraggableShell,
  WorkOrderDroppableShell,
  shouldMountTicketDraggable,
  shouldMountWorkOrderDraggable,
  shouldMountWorkOrderDroppable,
} from './work-order-card-dnd';

export interface WorkOrderKanbanCardProps {
  workOrder: KanbanWorkOrderView;
  canManage: boolean;
  organizationId: string;
  selected: boolean;
  activeDragKind: KanbanActiveDragKind;
  activeDragId: string | null;
  onSelectWorkOrder: (workOrderId: string) => void;
  onOpenMoveTicket: (workOrderId: string, ticketId: string) => void;
}

type CardChromeProps = {
  workOrder: KanbanWorkOrderView;
  canManage: boolean;
  organizationId: string;
  selected: boolean;
  activeDragKind: KanbanActiveDragKind;
  activeDragId: string | null;
  onSelectWorkOrder: (workOrderId: string) => void;
  onOpenMoveTicket: (workOrderId: string, ticketId: string) => void;
};

/**
 * Presentational card body. Kept free of DnD hooks so shell move-updates do
 * not recreate this tree (parent passes a stable element identity).
 */
const WorkOrderKanbanCardChrome = memo(function WorkOrderKanbanCardChrome({
  workOrder,
  canManage,
  organizationId,
  selected,
  activeDragKind,
  activeDragId,
  onSelectWorkOrder,
  onOpenMoveTicket,
}: CardChromeProps) {
  const { t } = useTranslation('maintenance');
  const canDragWorkOrder =
    canManage &&
    workOrder.status !== 'CLOSED' &&
    workOrder.status !== 'CANCELLED' &&
    workOrder.status !== 'ARCHIVED';

  const handleSelect = useCallback(() => {
    onSelectWorkOrder(workOrder.id);
  }, [onSelectWorkOrder, workOrder.id]);

  const handleOpenMoveTicket = useCallback(
    (ticketId: string) => {
      onOpenMoveTicket(workOrder.id, ticketId);
    },
    [onOpenMoveTicket, workOrder.id],
  );

  const ticketLabel =
    workOrder.tickets.length === 1
      ? t('workOrders.kanban.ticketCountOne')
      : t('workOrders.kanban.ticketCountMany', {
          count: workOrder.tickets.length,
        });

  return (
    <div
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSelect();
        }
      }}
      style={{
        background: WORK_ORDER_UI.cardBg,
        borderRadius: 12,
        border: selected
          ? `2px solid ${WORK_ORDER_UI.primary}`
          : `1px solid ${WORK_ORDER_UI.cardBorder}`,
        cursor: 'pointer',
        padding: 12,
        boxShadow: selected
          ? '0 4px 14px rgba(16,185,129,0.18)'
          : '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        {canDragWorkOrder ? <WorkOrderCardDragHandle /> : null}

        <div style={{ minWidth: 0, flex: 1 }}>
          <Space
            style={{ width: '100%', justifyContent: 'space-between' }}
            align="start"
          >
            <Typography.Text strong style={{ fontSize: 13 }}>
              {workOrder.code}
            </Typography.Text>
            <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
              {workOrder.externalIdentifier ? (
                <Tag
                  style={{
                    margin: 0,
                    ...softBadgeStyle(WORK_ORDER_UI.ticketType.CURATIVE),
                  }}
                >
                  {t('workOrders.kanban.externalOt', {
                    ot: workOrder.externalIdentifier,
                  })}
                </Tag>
              ) : null}
              <Tag
                style={{
                  margin: 0,
                  ...softBadgeStyle(
                    WORK_ORDER_UI.priority[
                      workOrder.highestPriority as keyof typeof WORK_ORDER_UI.priority
                    ] ?? WORK_ORDER_UI.priority.MEDIUM,
                  ),
                }}
              >
                {t(`workOrders.priority.${workOrder.highestPriority}`)}
              </Tag>
            </Space>
          </Space>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 8,
              color: '#64748b',
            }}
          >
            <EnvironmentOutlined style={{ fontSize: 12 }} />
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, margin: 0 }}
            >
              {workOrder.siteName}
            </Typography.Text>
          </div>

          <div style={{ marginTop: 6 }}>
            <Typography.Text
              style={{ fontSize: 12, color: WORK_ORDER_UI.suggestionLink }}
            >
              {ticketLabel}
            </Typography.Text>
          </div>

          {workOrder.assignedTechnicianName ? (
            <div style={{ marginTop: 8 }}>
              <TechnicianNameBadge name={workOrder.assignedTechnicianName} />
            </div>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          marginTop: 10,
        }}
      >
        {workOrder.tickets.map((ticket) => (
          <WorkOrderTicketChip
            key={ticket.id}
            ticket={ticket}
            workOrderId={workOrder.id}
            organizationId={organizationId}
            disabled={!canManage}
            mountDraggable={shouldMountTicketDraggable(
              activeDragKind,
              activeDragId,
              ticket.id,
              !canManage,
            )}
            onOpenMoveTicket={
              ticket.needsFollowUp ? handleOpenMoveTicket : undefined
            }
          />
        ))}
      </div>
    </div>
  );
});

export const WorkOrderKanbanCard: FC<WorkOrderKanbanCardProps> = memo(
  function WorkOrderKanbanCard({
    workOrder,
    canManage,
    organizationId,
    selected,
    activeDragKind,
    activeDragId,
    onSelectWorkOrder,
    onOpenMoveTicket,
  }) {
    const canDragWorkOrder =
      canManage &&
      workOrder.status !== 'CLOSED' &&
      workOrder.status !== 'CANCELLED' &&
      workOrder.status !== 'ARCHIVED';
    const mountDraggable = shouldMountWorkOrderDraggable(
      activeDragKind,
      activeDragId,
      workOrder.id,
      canDragWorkOrder,
    );
    const mountDroppable = shouldMountWorkOrderDroppable(
      activeDragKind,
      canManage,
    );

    const chrome = (
      <WorkOrderKanbanCardChrome
        workOrder={workOrder}
        canManage={canManage}
        organizationId={organizationId}
        selected={selected}
        activeDragKind={activeDragKind}
        activeDragId={activeDragId}
        onSelectWorkOrder={onSelectWorkOrder}
        onOpenMoveTicket={onOpenMoveTicket}
      />
    );

    if (mountDraggable && mountDroppable) {
      return (
        <WorkOrderDraggableShell
          workOrderId={workOrder.id}
          workOrderCode={workOrder.code}
        >
          <WorkOrderDroppableShell
            workOrderId={workOrder.id}
            status={workOrder.status}
          >
            {chrome}
          </WorkOrderDroppableShell>
        </WorkOrderDraggableShell>
      );
    }

    if (mountDroppable) {
      return (
        <WorkOrderDroppableShell
          workOrderId={workOrder.id}
          status={workOrder.status}
        >
          {chrome}
        </WorkOrderDroppableShell>
      );
    }

    if (mountDraggable) {
      return (
        <WorkOrderDraggableShell
          workOrderId={workOrder.id}
          workOrderCode={workOrder.code}
        >
          {chrome}
        </WorkOrderDraggableShell>
      );
    }

    return chrome;
  },
);
